import { Button, Chip, Input, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { StringListField, WorkingHoursField } from '@/routes/navigation/SettingListFields';
import { SETTING_JSON_EDITORS } from '@/routes/navigation/settingEditors';
import { TranslatableField } from '@/components/form/TranslatableField';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/useAuth';
import type { TranslatedValue } from '@/lib/forms/translatable';

import type { SettingGroupSummary, SettingRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/settings/:group`.
 *
 * §3 splits this one in two: `settings.view` goes to the Super Admin and the
 * Content Editor, `settings.update` to the Super Admin alone. So a Content
 * Editor sees every field and no save button — which is honest, and better
 * than a screen that looks editable and refuses.
 *
 * The controls come from §8's `type` column rather than from a list of keys
 * here: a `boolean` is a checkbox, a `media` is a path, a translatable `text`
 * is a textarea per locale. A setting added by a later migration therefore
 * appears with the right control and no change to this file.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  const groups = useQuery({
    queryKey: ['admin', 'settings', 'groups'],
    queryFn: () => apiFetch<{ data: SettingGroupSummary[] }>('/admin/settings/groups'),
  });

  const available = groups.data?.data ?? [];
  const current = rest === '' ? (available[0]?.group ?? '') : rest;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.settings')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('settings.intro')}</p>
      </div>

      {groups.isError ? <FormError message={t('errors.generic')} /> : null}

      <nav aria-label={t('settings.groups')}>
        <ul className="flex flex-wrap gap-2">
          {available.map((group) => (
            <li key={group.group}>
              <NavLink
                to={`/settings/${group.group}`}
                className={({ isActive }) =>
                  [
                    'inline-flex rounded-full px-4 py-2 text-body-sm font-semibold transition-colors',
                    isActive || group.group === current
                      ? 'bg-navy-600 text-white'
                      : 'bg-surface-subtle text-secondary hover:text-primary',
                  ].join(' ')
                }
              >
                {t(`settings.groupNames.${group.group}`, group.group)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {current === '' ? (
        <Spinner size="lg" label={t('common.loading')} />
      ) : (
        <GroupForm key={current} group={current} />
      )}
    </div>
  );
}

function GroupForm({ group }: { group: string }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const mayEdit = can('settings.update');

  const [values, setValues] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const settings = useQuery({
    queryKey: ['admin', 'settings', group],
    queryFn: () => apiFetch<{ data: SettingRow[] }>('/admin/settings', { query: { group } }),
  });

  const rows = settings.data?.data ?? [];

  // The loaded values, once, and whatever has been typed since. Not `useState`
  // with an initialiser: the rows arrive after the first render.
  const current = values ?? Object.fromEntries(rows.map((row) => [row.key, row.value]));

  const save = useMutation({
    mutationFn: () =>
      apiFetch('/admin/settings', {
        method: 'PUT',
        body: {
          settings: rows.map((row) => ({ key: row.key, value: current[row.key] ?? null })),
        },
      }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings', group] });
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
    },
  });

  if (settings.isPending) {
    return <Spinner size="lg" label={t('common.loading')} />;
  }

  const set = (key: string, value: unknown): void => {
    setSaved(false);
    setValues({ ...current, [key]: value });
  };

  return (
    <form
      className="max-w-3xl space-y-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <FormError message={error} />

      {rows.map((row) => (
        <SettingField
          key={row.key}
          setting={row}
          value={current[row.key]}
          disabled={!mayEdit}
          onChange={(value) => set(row.key, value)}
        />
      ))}

      {mayEdit ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>

          {saved ? (
            <span role="status" className="text-body-sm text-secondary">
              {t('settings.saved')}
            </span>
          ) : null}
        </div>
      ) : (
        // §3 — the Content Editor reads these and does not write them. Said
        // rather than left to a button that would 403.
        <p className="text-body-sm text-secondary">{t('settings.readOnly')}</p>
      )}
    </form>
  );
}

/** One setting, with the control §8's `type` calls for. */
function SettingField({
  setting,
  value,
  disabled,
  onChange,
}: {
  setting: SettingRow;
  value: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
}) {
  const { t } = useTranslation();

  // The key is the label. §8's keys are `contact_email`, `map_embed_url` — the
  // names their consumers use — and a translated label per key would be
  // twenty-four strings that go stale the moment a key is added.
  const label = t(`settings.keys.${setting.key}`, setting.key.replace(/_/g, ' '));

  const meta = setting.is_public ? null : <Chip tone="neutral">{t('settings.private')}</Chip>;

  if (setting.is_translatable) {
    return (
      <div className="space-y-1">
        <TranslatableField
          label={label}
          name={setting.key}
          multiline={setting.type === 'text'}
          rows={setting.type === 'text' ? 3 : undefined}
          value={(typeof value === 'object' && value !== null ? value : {}) as TranslatedValue}
          onChange={onChange}
          required={false}
          disabled={disabled}
        />
        {meta}
      </div>
    );
  }

  if (setting.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-body-sm text-primary">
        <input
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        />
        {label}
        {meta}
      </label>
    );
  }

  /*
   * §8's `json` is three settings, and all three have a form.
   *
   * They were all edited as raw JSON, which asked an administrator changing a
   * phone number to get a pair of brackets, four quotes and a comma right — and
   * silently kept the old value when they did not. Two are a list of strings and
   * the third is the opening-times table; none needs a text editor.
   *
   * Dispatched by key rather than by a new `type`, because the shapes belong to
   * these keys rather than to a category: a generic "list of strings" would
   * still leave `working_hours` needing its own form. Anything else typed `json`
   * falls through to the textarea, which is the honest answer for a shape nobody
   * has designed a form for — `SETTING_JSON_EDITORS` is the list, and
   * `SettingsPage.test.tsx` asserts every seeded `json` key is in it.
   */
  if (setting.type === 'json') {
    const kind = SETTING_JSON_EDITORS[setting.key];

    if (kind === 'hours') {
      return (
        <WorkingHoursField
          label={label}
          value={value}
          disabled={disabled}
          onChange={onChange}
          meta={meta}
        />
      );
    }

    if (kind !== undefined) {
      return (
        <StringListField
          label={label}
          value={value}
          kind={kind}
          disabled={disabled}
          onChange={onChange}
          meta={meta}
        />
      );
    }

    return (
      <JsonField label={label} value={value} disabled={disabled} onChange={onChange} meta={meta} />
    );
  }

  return (
    <div className="space-y-1">
      <Input
        label={label}
        type={setting.type === 'number' ? 'number' : 'text'}
        value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
        disabled={disabled}
        onChange={(event) =>
          onChange(setting.type === 'number' ? Number(event.target.value) : event.target.value)
        }
      />
      {meta}
    </div>
  );
}

function JsonField({
  label,
  value,
  disabled,
  onChange,
  meta,
}: {
  label: string;
  value: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
  meta: React.ReactNode;
}) {
  const { t } = useTranslation();

  const [text, setText] = useState(() => JSON.stringify(value ?? null, null, 2));
  const [invalid, setInvalid] = useState(false);

  return (
    <label className="block space-y-1">
      <span className="block text-body-sm font-semibold text-primary">{label}</span>

      <textarea
        rows={5}
        disabled={disabled}
        value={text}
        aria-invalid={invalid}
        className="w-full rounded-card border border-subtle bg-surface p-3 font-mono text-caption text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        onChange={(event) => {
          setText(event.target.value);

          try {
            onChange(JSON.parse(event.target.value));
            setInvalid(false);
          } catch {
            // Kept as typed. The save button stays live because the last valid
            // value is what would be sent; the field says it is not being used.
            setInvalid(true);
          }
        }}
      />

      {invalid ? (
        <span role="alert" className="block text-caption text-danger">
          {t('settings.invalidJson')}
        </span>
      ) : null}

      {meta}
    </label>
  );
}
